import AwesomeDebouncePromise from "awesome-debounce-promise";
import { removeFalsy } from "helpers/formatter";
import { TMessage } from "models/message";
import apiService from "modules/api/apiService";
import {
  CHANGELOG_URL,
  MESSAGE_URL,
  NEW_MESSAGE_URL,
  SEARCH_MEMBERS_URL,
} from "modules/api/endpoint";
import { API_STATUS } from "modules/api/type";
import { useState } from "react";
import { useAsync } from "react-async-hook";
import useConstant from "use-constant";

import {
  ChangelogResponse,
  MessageListDonePayload,
  NewMessageSuccessPayload,
} from "./messageActions";

export const getChangelog = async (): Promise<ChangelogResponse> => {
  const res = await apiService.get(CHANGELOG_URL);
  if (res.status === API_STATUS.success) {
    return res.data;
  }

  return {
    changelog: [],
    currentVersion: null,
  };
};
export const getNewMessage = async (): Promise<NewMessageSuccessPayload> => {
  const res = await apiService.get(NEW_MESSAGE_URL);
  if (res.status === API_STATUS.success) {
    return { count: res.data.payload.count };
  }

  return { count: 0 };
};

export const getMessageList = async (params = {}): Promise<MessageListDonePayload> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  const res = await apiService.get(`${MESSAGE_URL}${queryParams ? `?${queryParams}` : ""}`);
  if (res.status === API_STATUS.success) {
    return { data: res.data.payload.data, pagination: res.data.payload.pagination };
  }

  return { error: res };
};

export const getMessage = async (
  id: number
): Promise<{
  success: boolean;
  data?: TMessage;
}> => {
  try {
    const results = await apiService.get(`${MESSAGE_URL}/${id}`);

    return {
      data: results.data.payload,
      success: true,
    };
  } catch (error) {
    return { success: false };
  }
};

export const deleteMessage = async (id: number) => {
  return apiService.del(`${MESSAGE_URL}/${id}`);
};

export const markMessageAsRead = async (id: number, markReadAs: boolean) => {
  return apiService.put(
    `${MESSAGE_URL}/${id}/${markReadAs ? `mark-as-read` : `mark-as-unread`}`,
    {}
  );
};

export const searchMembers = async (str: string = "", filter: string = "") => {
  const params = `${str?.length ? `search=${str}` : ""}` + filter;

  //console.log(`Params are ${JSON.stringify(params)}`);
  return apiService.get(`${SEARCH_MEMBERS_URL}/?${params}`);
};

export const sendMessage = async (params: any) => {
  try {
    await apiService.post(`${MESSAGE_URL}`, params);

    return {
      success: true,
    };
  } catch (e) {
    return {
      success: false,
    };
  }
};

const useSearchMembers = () => {
  const [inputText, setInputText] = useState("");

  // Debounce the original search async function
  const debouncedSearchMembers = useConstant(() => AwesomeDebouncePromise(searchMembers, 300));

  const search = useAsync(
    async (text: string) => {
      return debouncedSearchMembers(text);
    },
    [inputText]
  );

  return {
    inputText,
    search,
    setInputText,
  };
};

export default {
  deleteMessage,
  getMessageList,
  getNewMessage,
  searchMembers,
  sendMessage,
  useSearchMembers,
};
